/* @noflow FIXME for real, I don't think the Notice works */
import gql from 'graphql-tag';
import * as React from 'react';
import invariant from 'invariant';
import { connect } from 'react-redux';
import { graphql, compose } from 'react-apollo';

import { bindActionCreators } from 'redux';
import { withRouter } from 'app/context/Router';
import { ORG_RADIOS } from 'admin/components/AdminUpgradePlanDefinitionForm';
import { REQUESTER_ROLE_OPTIONS } from 'admin/constants/AdminUpgrade';
import {
  ORG_TYPE_STRING,
  conditionallyAddPlus,
  getMarketingExperimentFields,
} from 'admin/reducers/adminUpgradePage';
import { showToast } from 'reducers/toasts';
import assertions from 'lib/assertions';
import Button from 'components/Button';
import Buttons from 'components/Buttons';
import Columns2 from 'components/Columns2';
import CurrentUser from 'auth/CurrentUser';
import errorLogger from 'utils/errorLogger';
import Field from 'components/Field';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
import Label from 'components/Label';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import Notice from 'components/Notice';
import OrganizationPicker from 'components/OrganizationPicker';
import PhoneNumberMaskedInput from 'components/PhoneNumberMaskedInput';
import Radio from 'components/Radio';
import Row from 'components/Row';
import Select from 'components/Select';
import Text from 'components/Text';
import Textarea from 'components/Textarea';
import EventTracking from 'lib/EventTracking';

import type { Fields } from 'admin/reducers/adminUpgradePage';

// from SelfServeHelpScreenSource https://github.com/remind101/r101-apollo/blob/master/src/data/schemas/Quote.js#L78
export const SOURCES = {
  marketing: 'marketing_page',
  onboarding: 'admin_onboarding',
  selfServe: 'self_serve',
};

type SelfServeRequestHelpMutationData = {
  organizationUuid: string,
  requesterFirstName: string,
  requesterLastName: string,
  requesterEmail: string,
  requesterPhoneNumber: string,
  studentsCount: number,
  teachersCount: number,
  quoteUuid?: ?string,
};

type Props = {
  router: *,
  fields: Fields,
  pageIndex: number,
  onDone: Function,
  showToast: (text: string, kind: 'success' | 'error') => void,
  requestHelp: (data: SelfServeRequestHelpMutationData) => Promise<{ data: Object }>,
  orgTypeString: string,
  quoteUuid?: ?string,
  organizationUuid?: ?string,
  source?: string,
  utmParams?: UTMParams,
};

type State = {
  requesterPhoneNumber?: ?string,
  requesterEmail?: ?string,
  requesterFirstName?: string,
  requesterLastName?: string,
  studentsCount?: ?number,
  teachersCount?: ?number,
  organizationUuid?: ?string,
  comment?: ?string,
  orgType: string,
  orgTypeError: ?string,
  requesterRole?: string,
  submitting: boolean,
  studentEnrollmentFieldValue: ?number,
  teachersOnStaffFieldValue: ?number,
};

class AdminUpgradeHelpForm extends React.Component<Props, State> {
  state: State = {
    submitting: false,
    studentEnrollmentFieldValue: null,
  };

  static trackingPrefix = 'admin_self_serve.help_form';

  componentWillMount() {
    const organizationUuid =
      this.props.fields.get('organizationUuid') || this.props.organizationUuid;
    if (organizationUuid) {
      this.setState({ organizationUuid });
      this.track('select_org.click');
    }

    const studentsCount = this.props.fields.get('studentsCount');
    if (studentsCount) {
      this.setState({ studentsCount });
      this.track('student_enrollment.click');
    }

    const teachersCount = this.props.fields.get('teachersCount');
    if (teachersCount) {
      this.setState({ teachersCount });
      this.track('teachers_on_staff.click');
    }

    const requesterPhoneNumber = this.props.fields.get('requesterPhoneNumber');
    if (requesterPhoneNumber) {
      this.setState({ requesterPhoneNumber });
      this.track('phone_number.click');
    }

    if (CurrentUser.isSignedIn()) {
      const user = CurrentUser.getUser();

      this.setState({
        requesterFirstName: user.getFirstName(),
        requesterLastName: user.getLastName(),
        requesterEmail: user.getEmail(),
        requesterRole: user.getUserSelectedRole(),
      });
    }
  }

  track = (key: string) => {
    EventTracking.trackKey(`${AdminUpgradeHelpForm.trackingPrefix}.${key}`);
  };

  setStateField = (name: string, value: *) => {
    this.setState({ [name]: value });
  };

  getSource = () => {
    return (
      this.props.source ||
      (window.document.referrer === LinkConstants.PLANS ? SOURCES.marketing : undefined)
    );
  };

  handleTeachersCountFocus = () => {
    this.track('teachers_on_staff.click');

    if (this.state.orgType) {
      return;
    }

    this.setState({ orgTypeError: 'You must select an organization type.' });
  };

  handleSubmit = () => {
    const {
      studentsCount,
      teachersCount,
      organizationUuid,
      requesterFirstName,
      requesterLastName,
      requesterEmail,
      requesterRole,
      comment,
    } = this.state;
    const { quoteUuid, utmParams } = this.props;

    const requesterPhoneNumber = conditionallyAddPlus(this.state.requesterPhoneNumber);

    invariant(
      studentsCount &&
        teachersCount &&
        organizationUuid &&
        requesterPhoneNumber &&
        requesterFirstName &&
        requesterLastName &&
        requesterEmail,
      'missing data in AdminUpgradeHelpForm submit'
    );

    this.setStateField('submitting', true);

    this.props
      .requestHelp({
        organizationUuid,
        studentsCount,
        teachersCount,
        requesterFirstName,
        requesterLastName,
        requesterRole,
        requesterEmail,
        requesterPhoneNumber,
        quoteUuid,
        comment,
        source: this.getSource(),
        screen: `help_request_screen_${this.props.pageIndex + 1}`,
        utmMedium:
          utmParams && utmParams.utm_medium
            ? utmParams.utm_medium
            : this.props.router.location.query['utm_medium'],
        utmSource:
          utmParams && utmParams.utm_source
            ? utmParams.utm_source
            : this.props.router.location.query['utm_source'],
        utmContent: this.props.router.location.query['utm_content'],
        utmCampaign:
          utmParams && utmParams.utm_campaign
            ? utmParams.utm_campaign
            : this.props.router.location.query['utm_campaign'],
        ...getMarketingExperimentFields(),
      })
      .then(({ data }) => {
        if (data.sendSelfServeHelpRequest.error) {
          errorLogger('AdminUpgradeHelpForm error submitting help request')(
            data.sendSelfServeHelpRequest.error
          );
          if (data.sendSelfServeHelpRequest.error.fieldErrors) {
            this.props.showToast(
              data.sendSelfServeHelpRequest.error.fieldErrors[0].message,
              'error'
            );
          } else {
            this.props.showToast('Oops! Something went wrong.', 'error');
          }
          this.setStateField('submitting', false);
        } else {
          invariant(this.props.onDone, 'onDone is falsey in AdminUpgradeHelpForm');
          this.props.onDone();
          this.props.showToast('Thanks for reaching out! We will be in touch shortly.', 'success');
        }
      })
      .catch(() => {
        errorLogger('AdminUpgradeHelpForm error submitting help request');
        this.props.showToast('Oops! Something went wrong.', 'error');
        this.setStateField('submitting', false);
      });

    this.track('submit.click');
  };

  isValid = () => {
    const {
      studentsCount,
      teachersCount,
      organizationUuid,
      requesterPhoneNumber,
      requesterFirstName,
      requesterLastName,
      requesterEmail,
      orgType,
    } = this.state;
    return (
      studentsCount &&
      teachersCount &&
      organizationUuid &&
      requesterPhoneNumber &&
      requesterFirstName &&
      requesterLastName &&
      requesterEmail &&
      orgType
    );
  };

  setOrgType = (type: string) => {
    this.setStateField('orgType', type);
    const { orgType } = this.state;
    if (orgType && orgType !== type) {
      this.setStateField('organizationUuid', null);
      this.setStateField('organizationAddress', null);
    }
    this.setState({ orgTypeError: null });
    this.track('org_type.click');
  };

  render() {
    const { orgType, orgTypeError } = this.state;

    return (
      <div>
        <Field error={orgTypeError}>
          <Label>Organization type</Label>
          {ORG_RADIOS.map(o => (
            <Radio
              inline
              name="help_org"
              key={o.key}
              label={o.name}
              onChange={() => this.setOrgType(o.key)}
              checked={orgType === o.key}
            />
          ))}
        </Field>
        {orgType && (
          <FormElement
            key={orgType}
            label={ORG_TYPE_STRING[this.state.orgType]}
            onValidChange={o => {
              this.track('select_org.click');
              this.setStateField('organizationUuid', o.uuid);
            }}
            element={OrganizationPicker}
            elementProps={{
              type: orgType === 'district' ? 'district' : 'school',
              initialOrganizationUuid: this.state.organizationUuid,
              onClickInteraction: () => this.track('org_picker.click'),
            }}
          />
        )}
        <Columns2 gutter="small" reset="tablet" resetFlush>
          <FormElement
            label="Estimated teachers on staff"
            validate={e => {
              assertions.notEmpty(e.target.value);
              assertions.positiveNumber(e.target.value);
              assertions.integer(e.target.value);
              assertions.signedInt32(Number(e.target.value));
            }}
            onValidChange={e => this.setStateField('teachersCount', parseInt(e.target.value, 10))}
            onInvalidChange={() => this.setStateField('teachersCount', undefined)}
            element={Input}
            elementProps={{
              defaultValue: this.props.fields.get('teachersCount'),
              onFocus: this.handleTeachersCountFocus,
              onBlur: e => this.setStateField('teachersOnStaffFieldValue', e.target.value),
            }}
          />
          <FormElement
            label="Estimated student enrollment"
            validate={e => {
              assertions.notEmpty(e.target.value);
              assertions.positiveNumber(e.target.value);
              assertions.integer(e.target.value);
              assertions.signedInt32(Number(e.target.value));
              assertions.valueGreaterThan(5, e.target.value, 'Must include more than five people');
            }}
            onValidChange={e => this.setStateField('studentsCount', parseInt(e.target.value, 10))}
            onInvalidChange={() => this.setStateField('studentsCount', undefined)}
            element={Input}
            elementProps={{
              defaultValue: this.props.fields.get('studentsCount'),
              onFocus: () => this.track('student_enrollment.click'),
              onBlur: e => this.setStateField('studentEnrollmentFieldValue', e.target.value),
            }}
          />
        </Columns2>
        {this.state.studentEnrollmentFieldValue &&
          this.state.studentEnrollmentFieldValue <= 5 && (
            <Row size="medium">
              <Notice color="polar-bear" noShadow data-test="student-enrollment-too-small">
                <Text size="xsmall">
                  Remind plans are only available for schools and districts, but you can always
                  start with an individual account! Click <Link to="/">here</Link> to sign up for
                  free.
                </Text>
              </Notice>
            </Row>
          )}
        {!CurrentUser.isSignedIn() && (
          <Columns2 gutter="small" reset="tablet" resetFlush>
            <FormElement
              label="Your first name"
              validate={e => {
                assertions.notEmpty(e.target.value);
              }}
              onValidChange={e => this.setStateField('requesterFirstName', e.target.value)}
              onInvalidChange={() => this.setStateField('requesterFirstName', undefined)}
              element={Input}
              elementProps={{
                onFocus: () => this.track('first_name.click'),
              }}
            />
            <FormElement
              label="Your last name"
              validate={e => {
                assertions.notEmpty(e.target.value);
              }}
              onValidChange={e => this.setStateField('requesterLastName', e.target.value)}
              onInvalidChange={() => this.setStateField('requesterLastName', undefined)}
              element={Input}
              elementProps={{
                onFocus: () => this.track('last_name.click'),
              }}
            />
          </Columns2>
        )}
        <Columns2 gutter="small" reset="tablet" resetFlush>
          <FormElement
            showErrorAfterBlur
            label="Your phone number"
            validate={e => {
              assertions.number(e.raw, 'Invalid phone number.');
              assertions.completePhoneNumber(e.raw, 'Invalid phone number.');
            }}
            onValidChange={e => this.setStateField('requesterPhoneNumber', e.raw)}
            onInvalidChange={() => this.setStateField('requesterPhoneNumber', undefined)}
            element={PhoneNumberMaskedInput}
            elementProps={{
              value: this.state.requesterPhoneNumber,
              onFocus: () => this.track('phone_number.click'),
            }}
          />
          <FormElement
            showErrorAfterBlur
            label="Your official email address"
            validate={e => {
              assertions.email(e.target.value);
              assertions.notEmpty(e.target.value);
            }}
            onValidChange={e => this.setStateField('requesterEmail', e.target.value)}
            onInvalidChange={() => this.setStateField('requesterEmail', undefined)}
            element={Input}
            elementProps={{
              onFocus: () => this.track('email.click'),
            }}
          />
        </Columns2>
        <Columns2 gutter="small" reset="tablet" resetFlush>
          <FormElement
            label="Role"
            validate={role => {
              assertions.notEmpty(role);
            }}
            onValidChange={role => this.setStateField('requesterRole', role)}
            onInvalidChange={() => this.setStateField('requesterRole', undefined)}
            element={Select}
            elementProps={{
              onFocus: () => this.track('role.click'),
              options: REQUESTER_ROLE_OPTIONS,
              value: this.state.requesterRole,
            }}
          />
        </Columns2>
        {orgType && (
          <FormElement
            showErrorAfterBlur
            label={`Why are you interested in upgrading to a ${
              ORG_TYPE_STRING[this.state.orgType]
            } Plan?`}
            onValidChange={e => this.setStateField('comment', e.target.value)}
            onInvalidChange={() => this.setStateField('comment', undefined)}
            element={Textarea}
            elementProps={{
              value: this.state.comment,
              onFocus: () => this.track('details.click'),
              placeholder: `Tell us why you're considering implementing Remind at your ${ORG_TYPE_STRING[
                this.state.orgType
              ].toLowerCase()}, what questions you have, and anything else that will allow us to follow up in a helpful way.`,
            }}
          />
        )}
        <Buttons>
          <Button onClick={this.handleSubmit} disabled={!this.isValid() || this.state.submitting}>
            Submit
          </Button>
        </Buttons>
      </div>
    );
  }
}

const withRequestHelpMutation = graphql(
  gql`
    mutation SelfServeRequestHelpMutation($input: SelfServeHelpInput!) {
      sendSelfServeHelpRequest(input: $input) {
        error {
          message
          code
          fieldErrors {
            field
            message
          }
        }
      }
    }
  `,
  {
    props: ({ _, mutate }) => ({
      requestHelp: (input: SelfServeRequestHelpMutationData) => {
        return mutate({
          variables: { input },
        });
      },
    }),
  }
);

function mapStateToProps(state) {
  return {
    fields: state.adminUpgradePage.fields,
    pageIndex: state.adminUpgradePage.pageIndex,
  };
}

function mapDispatchToProps(dispatch: Function) {
  return bindActionCreators({ showToast }, dispatch);
}

export { AdminUpgradeHelpForm };

export default compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withRequestHelpMutation
)(AdminUpgradeHelpForm);
